import { MobilePageShell } from '@/components/app/mobile-page-shell';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInput,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  conversationStatusLabels,
  conversationStatusValues,
  messagingPlatformLabels,
  messagingPlatformValues,
  useConversationTabCounts,
  useListConversations,
  useSyncConversations,
} from '@/features/conversations/api';
import type {
  ConversationListItem,
  ConversationStatus,
  MessagingPlatform,
} from '@/features/conversations/api';
import {
  useActiveOrganization,
  useGetOrganizationMembers,
} from '@/features/organization';
import { stripBranchFromPath } from '@/features/organization-locations/branch-path';
import { cn } from '@/lib/utils';
import { useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ConversationInboxRow } from './conversation-inbox-row';
import {
  type ConversationTabValue,
  ConversationTabs,
} from './conversation-tabs';
import {
  ConversationsInboxEmpty,
  hasActiveInboxFilters,
} from './conversations-inbox-empty';
import { ConversationsInboxFilters } from './conversations-inbox-filters';
import { useInboxRowActions } from './inbox-row-actions';

function memberFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

function useConversationsInboxState(
  autoSync: boolean,
  controlledSearch?: { value: string; onChange: (value: string) => void },
  controlledPlatform?: { value: string; onChange: (value: string) => void },
  controlledTab?: {
    value: ConversationTabValue;
    onChange: (value: ConversationTabValue) => void;
  }
) {
  const idFromSearch = useRouterState({
    select: (s) => {
      // Branch URLs are `/dashboard/l/<branch>/clients/inbox`; without stripping
      // the branch the selected id was never read, so no row showed as selected.
      if (
        !stripBranchFromPath(s.location.pathname).includes(
          '/dashboard/clients/inbox'
        )
      ) {
        return undefined;
      }
      const raw = s.location.search as string | Record<string, unknown>;
      if (typeof raw === 'string') {
        const trimmed = raw.startsWith('?') ? raw.slice(1) : raw;
        return new URLSearchParams(trimmed).get('id') ?? undefined;
      }
      if (raw && typeof raw === 'object' && 'id' in raw) {
        const id = (raw as { id?: string }).id;
        return typeof id === 'string' ? id : undefined;
      }
      return undefined;
    },
  });

  const [ownTab, setOwnTab] = useState<ConversationTabValue>('from_ads');
  // The desktop Inbox page draws the tabs across its top, outside this panel;
  // when it does, its state wins.
  const tab = controlledTab?.value ?? ownTab;
  const setTab = controlledTab?.onChange ?? setOwnTab;
  // The search box can live outside the panel; when it does, that state wins.
  const [ownSearch, setOwnSearch] = useState('');
  const search = controlledSearch?.value ?? ownSearch;
  const setSearch = controlledSearch?.onChange ?? setOwnSearch;
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ownPlatform, setOwnPlatform] = useState<string>('all');
  const platformFilter = controlledPlatform?.value ?? ownPlatform;
  const setPlatformFilter = controlledPlatform?.onChange ?? setOwnPlatform;
  const apiPlatform =
    platformFilter === 'all'
      ? undefined
      : (platformFilter as MessagingPlatform);
  const { counts: tabCounts } = useConversationTabCounts(apiPlatform);

  const { data: activeOrg } = useActiveOrganization();
  const { members } = useGetOrganizationMembers(activeOrg?.id ?? '');

  const memberFirstNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) {
      map.set(member.user.id, memberFirstName(member.user.name));
    }
    return map;
  }, [members]);

  const apiStatus: ConversationStatus | undefined =
    statusFilter !== 'all' ? (statusFilter as ConversationStatus) : undefined;

  const {
    conversations: apiConversations,
    isLoading,
    isError,
  } = useListConversations({
    status: apiStatus,
    platform: apiPlatform,
    tab,
    limit: 100,
    offset: 0,
  });

  const conversations = apiConversations;

  const { syncConversations } = useSyncConversations();
  const hasSynced = useRef(false);
  useEffect(() => {
    // Admin org-override views are read-only, so the sync POST would be
    // rejected — skip it. The org's owners trigger syncs from their own session.
    if (!autoSync) return;
    if (!hasSynced.current) {
      hasSynced.current = true;
      syncConversations();
    }
  }, [autoSync, syncConversations]);

  const filtered = useMemo(() => {
    let items = conversations;

    if (platformFilter !== 'all') {
      items = items.filter((c) => c.platform === platformFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (c) =>
          c.externalUserName?.toLowerCase().includes(q) ||
          c.externalUserId.toLowerCase().includes(q) ||
          c.lastMessageContent?.toLowerCase().includes(q)
      );
    }

    return items;
  }, [conversations, search, platformFilter]);

  return {
    idFromSearch,
    tab,
    setTab,
    tabCounts,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    platformFilter,
    setPlatformFilter,
    memberFirstNameById,
    filtered,
    isLoading,
    isError,
  };
}

function ConversationListBody({
  conversations,
  isLoading,
  isError,
  selectedId,
  memberFirstNameById,
  variant,
  hasActiveFilters,
  onClearFilters,
  onSelectConversation,
  renderLeading,
  renderTrailing,
}: {
  conversations: ConversationListItem[];
  isLoading: boolean;
  isError: boolean;
  selectedId?: string;
  memberFirstNameById: Map<string, string>;
  variant: 'sidebar' | 'mobile' | 'card';
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onSelectConversation?: (id: string) => void;
  /** Desktop list: per-row checkbox and actions menu. */
  renderLeading?: (conversation: ConversationListItem) => ReactNode;
  renderTrailing?: (conversation: ConversationListItem) => ReactNode;
}) {
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex flex-col gap-2',
          variant === 'mobile' ? 'p-4' : 'p-3'
        )}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-center text-sm text-destructive">
        Failed to load conversations.
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <ConversationsInboxEmpty
        variant={variant === 'card' ? 'sidebar' : variant}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />
    );
  }

  // No day/date separators between rows: each row carries its own date
  // (`formatInboxTime`), so the list stays one continuous column.
  const rows = conversations.map((conv) => (
    <ConversationInboxRow
      key={conv.id}
      conversation={conv}
      isSelected={selectedId === conv.id}
      memberFirstNameById={memberFirstNameById}
      variant={variant}
      onSelect={onSelectConversation}
      leading={renderLeading?.(conv)}
      trailing={renderTrailing?.(conv)}
    />
  ));

  return (
    <div
      className={cn(variant === 'sidebar' && 'flex flex-col gap-2 p-3 pt-2')}
    >
      {rows}
    </div>
  );
}

export function ConversationsPanel({
  selectedId,
  variant = 'sidebar',
  onSelectConversation,
  autoSync = true,
  search: controlledSearch,
  onSearchChange,
  platform: controlledPlatform,
  onPlatformChange,
  tab: controlledTab,
  onTabChange,
}: {
  selectedId?: string;
  variant?: 'sidebar' | 'mobile' | 'card';
  /**
   * When provided, rows call this instead of navigating to
   * `/dashboard/conversations` — used by the admin panel for local selection.
   */
  onSelectConversation?: (id: string) => void;
  /**
   * Auto-trigger a conversation sync on mount. Disabled in the admin
   * (read-only) view, where the sync POST would be rejected.
   */
  autoSync?: boolean;
  /** Search owned by the page (its header field); hides the panel's own. */
  search?: string;
  onSearchChange?: (value: string) => void;
  /**
   * Platform filter owned by the page, so its tab counts and the list agree.
   * The panel still renders the filter button.
   */
  platform?: string;
  onPlatformChange?: (value: string) => void;
  /** View tab owned by the page, which draws the tabs itself (card variant). */
  tab?: ConversationTabValue;
  onTabChange?: (value: ConversationTabValue) => void;
}) {
  const {
    idFromSearch,
    tab,
    setTab,
    tabCounts,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    platformFilter,
    setPlatformFilter,
    memberFirstNameById,
    filtered,
    isLoading,
    isError,
  } = useConversationsInboxState(
    autoSync,
    controlledSearch !== undefined && onSearchChange
      ? { value: controlledSearch, onChange: onSearchChange }
      : undefined,
    controlledPlatform !== undefined && onPlatformChange
      ? { value: controlledPlatform, onChange: onPlatformChange }
      : undefined,
    controlledTab !== undefined && onTabChange
      ? { value: controlledTab, onChange: onTabChange }
      : undefined
  );

  const effectiveSelectedId = selectedId ?? idFromSearch;
  // Desktop list only, but hooks run unconditionally.
  const rowActions = useInboxRowActions(
    filtered,
    `${tab}|${platformFilter}|${search}`,
    tab === 'from_ads' || tab === 'needs_reply' || tab === 'cold'
  );
  const isMobile = variant === 'mobile';

  const inboxFiltersActive = hasActiveInboxFilters({
    search,
    platformFilter,
    statusFilter,
  });

  const clearInboxFilters = () => {
    setSearch('');
    setPlatformFilter('all');
    setStatusFilter('all');
  };

  if (isMobile) {
    return (
      <MobilePageShell
        className="bg-white"
        tabRoot
        title="Inbox"
        // Title in the floating header, on one line with the branch picker.
        titleInHeader
        toolbar={
          <div className="flex flex-col gap-3">
            <ConversationTabs
              value={tab}
              onChange={setTab}
              counts={tabCounts}
            />
            <ConversationsInboxFilters
              onPlatformFilterChange={setPlatformFilter}
              onSearchChange={setSearch}
              platformFilter={platformFilter}
              search={search}
            />
          </div>
        }
      >
        <div data-testid="conversation-list">
          <ConversationListBody
            conversations={filtered}
            hasActiveFilters={inboxFiltersActive}
            isError={isError}
            isLoading={isLoading}
            memberFirstNameById={memberFirstNameById}
            onClearFilters={clearInboxFilters}
            onSelectConversation={onSelectConversation}
            selectedId={effectiveSelectedId}
            variant="mobile"
          />
        </div>
      </MobilePageShell>
    );
  }

  if (variant === 'card') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col gap-3 border-b p-3.5">
          {controlledTab === undefined ? (
            <ConversationTabs
              value={tab}
              onChange={setTab}
              counts={tabCounts}
              className="flex-wrap overflow-visible"
            />
          ) : null}
          <div className="flex items-center gap-3">
            {/* Select all, in line with the row checkboxes below it. */}
            <div className="shrink-0 pl-0.5">{rowActions.selectAll}</div>
            <div className="min-w-0 flex-1">
              <ConversationsInboxFilters
                onPlatformFilterChange={setPlatformFilter}
                onSearchChange={setSearch}
                platformFilter={platformFilter}
                search={search}
              />
            </div>
          </div>
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          data-testid="conversation-list"
        >
          <ConversationListBody
            conversations={filtered}
            isLoading={isLoading}
            isError={isError}
            selectedId={effectiveSelectedId}
            memberFirstNameById={memberFirstNameById}
            variant="card"
            hasActiveFilters={inboxFiltersActive}
            onClearFilters={clearInboxFilters}
            onSelectConversation={onSelectConversation}
            renderLeading={rowActions.renderCheckbox}
            renderTrailing={rowActions.renderMenu}
          />
        </div>
        {rowActions.dialogs}
      </div>
    );
  }

  return (
    <>
      <SidebarHeader className="gap-3.5 border-b p-4">
        <div className="text-base font-medium text-foreground">
          Conversations
        </div>
        <ConversationTabs value={tab} onChange={setTab} counts={tabCounts} />
        <SidebarInput
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-7 flex-1 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {conversationStatusValues.map((s) => (
                <SelectItem key={s} value={s}>
                  {conversationStatusLabels[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="h-7 flex-1 text-xs">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {messagingPlatformValues.map((p) => (
                <SelectItem key={p} value={p}>
                  {messagingPlatformLabels[p as MessagingPlatform]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-0">
          <SidebarGroupContent>
            <ConversationListBody
              conversations={filtered}
              isLoading={isLoading}
              isError={isError}
              selectedId={effectiveSelectedId}
              memberFirstNameById={memberFirstNameById}
              variant="sidebar"
              hasActiveFilters={inboxFiltersActive}
              onClearFilters={clearInboxFilters}
              onSelectConversation={onSelectConversation}
            />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  );
}
